<template>
  <div>
    <tabs :tabs="tabs">
      <panel :title="title" class="payment-list">
        <div slot="toolbar" class="form-group payment-list-toolbar">
          <div v-if="!splitDisabled" class="mb-2">
            <w-input
              class="d-inline-block mr-2"
              :model="$schema.split.grant"
              :disabled="!splitEditable"
              @input="calculateContribution"
            />

            <w-input
              class="d-inline-block mr-2"
              :model="$schema.split.contribution"
              :disabled="!splitEditable"
              @input="calculateGrant"
            />

            <w-button v-if="!splitEditable" class="btn-primary mt-4" text="Zmień rozkład źródeł" @click="unlockSplit" />

            <w-button v-else class="btn-primary mt-4" text="Wylicz automatycznie" @click="lockSplit" />
          </div>

          <div class="mb-2">
            <w-select
              v-if="availableSchools.length"
              class="d-inline-block mr-2"
              style="width: 400px"
              :model="$schema.filters.selectedSchool"
              :values="availableSchools"
            />

            <w-select
              v-if="types && types.length"
              class="d-inline-block mr-2"
              style="width: 400px"
              :model="$schema.filters.selectedType"
              :values="types"
            />

            <w-select class="d-inline-block mr-2" :model="$schema.filters.year" :values="years" />

            <w-select class="d-inline-block month-select mr-2" :model="$schema.filters.month" :values="months" />

            <w-datepicker
              v-if="!koConfig.disablePaymentDate"
              class="d-inline-block mr-2"
              :model="$schema.submit.paymentDate"
            />

            <w-button class="btn-primary mb-2 mt-4" text="Pokaż" @click="reload" />
          </div>
        </div>

        <w-table
          id-key="documentId"
          :columns="columns"
          :actions="actions"
          :data="paymentList"
          :show-checkboxes="true"
          :hide-add-button="true"
          :hide-search-input="true"
          :per-page="-1"
          :total="1"
          :foot-data="footData"
          @checkAll="checkAll"
          @checked="updateSplit"
          @edit="editData"
          @restore="clearOverwriteData"
          @viewDocument="goToDocument"
        />

        <div slot="footer">
          <w-button class="btn-secondary mt-4" text="Wróć" @click="goBack" />

          <div class="float-right">
            <payment-pack-picker
              class="ml-2"
              :model="$schema.submit.packId"
              :procedure-id="procedureId"
              :title="koConfig.paymentPackTitle"
              :placeholder="koConfig.paymentPackPlaceholder"
            />

            <w-button
              class="btn-primary ml-2 mt-4"
              :text="submitDraftButtonText"
              :disabled="!checkedIds.length"
              @click="confirmSubmitDraft"
            />
          </div>

          <post-form
            v-if="!koConfig.disablePdfPreview"
            text="Podgląd PDF"
            class="float-right mt-4 mr-2"
            :url="downloadPdfUrl"
            :fields="downloadPdfParams"
          />
        </div>
      </panel>
    </tabs>

    <payment-data-modal :show="modals.edit" :data="dataToEdit" @close="closeModal('edit')" @save="saveOverwriteData" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';

import PaymentPackPicker from '@/components/formComponents/PaymentPackPicker';
import PaymentDataModal from '@/components/modals/PaymentDataModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import WTable from '@/components/table/Table';
import Tabs from '@/components/tabs/Tabs';
import { Errors } from '@/enums/errors.enum';
import { Options } from '@/enums/options.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { TableName } from '@/enums/tableName';
import { displayModalMixin } from '@/mixins/displayModal';
import { formMixin } from '@/mixins/form';
import { koMixin } from '@/mixins/ko';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/paymentList.schema';
import store from '@/store/store';
import { config } from '@/utils/config';
import { EDIT_PAYMENT_DATA_ACTION, RESTORE_PAYMENT_DATA_ACTION, VIEW_DOCUMENT_ACTION } from '@/utils/constants';
import { getYears, months } from '@/utils/dates';
import { calculateFootData } from '@/utils/footData';
import { tryParseInt } from '@/utils/number';
import { getPaymentList, getPaymentListVariables } from '@/utils/paymentList';
import { serverError } from '@/utils/serverError';

// @vuese
// Lista wypłat
// @group Procedure
export default {
  components: { Panel, PaymentDataModal, PaymentPackPicker, PostForm, Tabs, WTable },
  mixins: [formMixin, displayModalMixin, koMixin, notificationsMixin],
  schema,

  data() {
    const month = tryParseInt(this.$route.query.month);
    const split = store.state.procedure.paymentListSplit;
    const paymentDate = this.$route.query.paymentDate || new Date();

    return {
      model: {
        split: split
          ? {
              contribution: split.contribution * 100,
              grant: split.grant * 100
            }
          : {},
        filters: {
          month: month !== undefined ? month : new Date().getMonth(),
          year: tryParseInt(this.$route.query.year) || new Date().getFullYear(),
          selectedSchool: undefined
        },
        submit: { paymentDate: moment(paymentDate).format('YYYY-MM-DD') }
      },
      years: getYears(),
      splitEditable: false,
      tabs: [
        { title: 'Wszystkie', route: { name: 'procedurePaymentList', query: { selectedOnly: undefined } } },
        { title: 'Zaznaczone', route: { name: 'procedurePaymentList', query: { selectedOnly: 'true' } } }
      ],
      dataToEdit: undefined
    };
  },

  computed: {
    ...mapState({
      paymentList(state) {
        return state.procedure.paymentList
          .map(row => {
            const grant = (row.amount * this.$schema.split.grant.$model) / 100;
            const contribution = row.amount - grant;
            row.grant = Math.round(grant * 100) / 100;
            row.contribution = Math.round(contribution * 100) / 100;
            return row;
          })
          .filter(row => !this.$route.query.selectedOnly || row.checked);
      },

      split: state =>
        state.procedure.paymentListSplit || {
          contribution: 0,
          grant: 0
        },

      splitDisabled(state) {
        return (
          state.setting.settings[PublicSettings.PAYMENT_NO_SPLIT]?.value === 'true' ||
          this.selectedPaymentTypeData?.splitDisabled
        );
      },

      ksatDisabled: state => state.setting.settings[PublicSettings.KSAT_DISABLED]?.value === 'true',

      columns: state =>
        state.setting.settings[PublicSettings.PAYMENT_COLUMNS]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.PAYMENT_COLUMNS].value).filter(column => !column.hideInView),

      types: state =>
        (state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES]?.value &&
          JSON.parse(state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES].value).map((type, index) => ({
            ...type,
            value: index
          }))) ||
        [],

      procedureId(state) {
        return tryParseInt(
          state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value,
          this.$route.params.procedureId
        );
      }
    }),
    ...mapGetters({ structures: 'auth/paymentListStructures' }),

    title() {
      return this.koConfig.title || 'Lista wypłat';
    },

    actions() {
      const actions = [VIEW_DOCUMENT_ACTION];

      if (!this.ksatDisabled) {
        actions.unshift(EDIT_PAYMENT_DATA_ACTION, {
          ...RESTORE_PAYMENT_DATA_ACTION,
          check: object => object.isOverwrite
        });
      }

      return actions;
    },

    selectedPaymentType() {
      return tryParseInt(this.$route?.query.selectedType, 0);
    },
    selectedPaymentTypeData() {
      if (this.types?.length) {
        return this.types[this.selectedPaymentType];
      }
    },
    availableSchools() {
      return this.structures.map(school => ({ text: school.name, value: school.id }));
    },
    months() {
      return months(this.model.filters.year);
    },
    checkedIds() {
      return this.paymentList.filter(row => !!row.checked).map(row => row.documentId);
    },

    downloadPdfParams() {
      const params = [];
      const options = getPaymentListVariables(this.$route, this.procedureId);
      const { paymentDate } = this.$schema.submit.$getData();
      options.documentIds = this.checkedIds;
      options.procedureId = options.id;
      options.paymentDate = paymentDate;
      params.push({ name: 'options', value: options });

      if (this.splitEditable) {
        const split = {};
        const { grant, contribution } = this.$schema.split.$getData();
        split.grant = grant / 100;
        split.contribution = contribution / 100;
        params.push({ name: 'split', value: split });
      }

      return params;
    },

    downloadPdfUrl() {
      return config.renderURL + '/pdf/preview/payment_list/' + this.procedureId;
    },

    footData() {
      return calculateFootData(
        this.paymentList.filter(row => !!row.checked),
        this.columns,
        { checkboxValue: 'Razem:' }
      );
    },

    submitDraftButtonText() {
      return this.koConfig.submitButtonText || `Przygotuj wysłyłkę do ${this.ksatDisabled ? 'MDOK' : 'KSAT'}`;
    }
  },

  watch: {
    split(newValue) {
      if (this.splitEditable) {
        return;
      }

      this.$schema.split.grant.$setData(newValue.grant * 100);
      this.$schema.split.contribution.$setData(newValue.contribution * 100);
    }
  },

  mounted() {
    this.$schema.filters.selectedSchool.$setData(this.$route.query.selectedSchool || this.availableSchools?.[0]?.value);
    if (this.types?.length) {
      this.$schema.filters.selectedType.$setData(this.selectedPaymentType);
    }
  },

  methods: {
    // @vuese
    // Otwiera modala z możliwością nadpisania danych
    editData(data) {
      this.$store
        .dispatch('optionValue/getByCode', {
          code: Options.DICTIONARY,
          entityType: TableName.status,
          entityId: data.statusId.toString()
        })
        .then(() => {
          this.dataToEdit = data;
          this.openModal('edit');
        })
        .catch(err => this.notifyError(Errors.CANNOT_GET_DICTIONARY));
    },

    // @vuese
    // Zapisuje dane do nadpisania
    saveOverwriteData(data) {
      this.$store.commit('procedure/overwritePaymentData', { id: data.id, overwriteData: data });
      this.closeModal('edit');
    },

    // @vuese
    // Czyści dane do nadpisania
    clearOverwriteData(data) {
      this.$store.commit('procedure/restorePaymentData', data.documentId);
    },

    // @vuese
    // Wraca do szczegółów procedury
    goBack() {
      this.$router.push({ name: 'procedureDetails' });
    },

    // @vuese
    // Pokazuje listę wypłat na wybraną datę
    reload() {
      const { year, month, selectedSchool, selectedType } = this.$schema.filters.$getData();
      const { paymentDate } = this.$schema.submit.$getData();
      this.$router.push({
        name: this.$route.name,
        query: { month, year, selectedSchool, selectedType, paymentDate, selectedOnly: this.$route.query.selectedOnly }
      });
    },

    // @vuese
    // Potwierdza przesłanie do KSAT
    confirmSubmitDraft() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }

      this.$store.dispatch('setConfirm', {
        closeButton: 'Nie',
        submitButton: 'Tak',
        title:
          this.koConfig.confirmText || 'Czy na pewno chcesz dołączyć zaznaczone pozycje do roboczej paczki płatności?',
        onSuccess: () => this.submitDraft()
      });
    },

    // @vuese
    // Przesyła listę do KSAT
    submitDraft() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }
      const data = getPaymentListVariables(this.$route, this.procedureId);
      if (!this.splitDisabled && this.splitEditable) {
        const { grant, contribution } = this.$schema.split.$getData();
        data.grant = grant / 100;
        data.contribution = contribution / 100;
      }
      data.documentIds = this.checkedIds;
      const { paymentDate, packId } = this.$schema.submit.$getData();
      data.paymentDate = paymentDate;
      data.paymentType = this.types?.length ? this.types[this.selectedPaymentType]?.name : undefined;
      data.packId = packId;
      data.overwriteData = this.paymentList
        .filter(row => row.isOverwrite)
        .map(row => ({ id: row.id, data: { bankAccountOwner: row.data.bankAccountOwner } }));
      data.structureIds = this.$schema.filters.selectedSchool.$model
        ? [this.$schema.filters.selectedSchool.$model]
        : undefined;
      this.$store
        .dispatch('procedure/paymentListSubmitDraft', data)
        .then(result => {
          const description = result.length
            ? result.join('<br/>') + '<br/>' + Errors.CONTACT_WITH_KSAT_ADMIN
            : undefined;
          this.$store.dispatch('setConfirm', {
            description,
            showCloseButton: false,
            submitButton: 'Ok',
            title: this.koConfig.successText || Successes.PAYMENT_LIST_SAVED,
            onSuccess: () => getPaymentList(this.$route, paymentDate)
          });
        })
        .catch(err => {
          this.$store.dispatch('setConfirm', {
            description: serverError(err) + '<br/>' + Errors.CHECK_SELECTED_DATA,
            showCloseButton: false,
            submitButton: 'Ok',
            title: this.koConfig.errorText || Errors.PAYMENT_LIST_CANNOT_SAVE
          });
        });
    },

    // @vuese
    // Zaznacza lub odznacza wszystko
    checkAll(checked) {
      this.paymentList.forEach(row => (row.checked = checked));
      this.updateSplit();
    },

    // @vuese
    // Odblokowuje edycję rozkładu źródeł wypłat
    unlockSplit() {
      this.$store.dispatch('setConfirm', {
        title: 'Czy na pewno chcesz zmienić rozkład źródeł wypłat?',
        description: 'Zmiana rozkładu źródeł wypłat może spowodować nieprawidłowe rozliczenie wypłat.',
        onSuccess: () => (this.splitEditable = true)
      });
    },

    // @vuese
    // Blokuje edycję rozkładu źródeł i wczytuje domyślne, pobrane z API
    lockSplit() {
      this.splitEditable = false;
      this.updateSplit();
    },

    // @vuese
    // Przelicza kwotę wkładu własnego
    calculateContribution() {
      this.calculateSplit('grant', 'contribution');
    },

    // @vuese
    // Przelicza kwotę dotacji
    calculateGrant() {
      this.calculateSplit('contribution', 'grant');
    },

    // @vuese
    // Przelicza kwotę źródła
    calculateSplit(changedFieldName, fieldToChange) {
      const changedField = this.$schema.split[changedFieldName];
      if (changedField.$model > 100) {
        changedField.$setData(100);
      }
      this.$schema.split[fieldToChange].$setData(100 - changedField.$model);
    },

    // @vuese
    // Pobiera nowy podział źródeł
    updateSplit() {
      if (this.splitEditable) {
        return;
      }

      if (this.split.contribution === 1 || this.split.grant === 0.8) {
        this.$schema.split.grant.$setData(this.split.grant * 100);
        this.$schema.split.contribution.$setData(this.split.contribution * 100);
      }

      const { paymentDate } = this.$schema.submit.$getData();

      const data = getPaymentListVariables(this.$route, this.procedureId);
      data.documentIds = this.paymentList.filter(row => !!row.checked).map(row => row.documentId);
      data.paymentDate = paymentDate;
      data.paymentType = this.selectedPaymentTypeData?.name;
      this.$store
        .dispatch('procedure/paymentListSplit', data)
        .then(split => {
          this.$schema.split.grant.$setData(split.grant * 100);
          this.$schema.split.contribution.$setData(split.contribution * 100);
        })
        .catch(err => this.notifyError(Errors.PAYMENT_LIST_CANNOT_GET));
    },

    // @vuese
    // Przejście do dokumentu
    goToDocument(data) {
      this.$router.push({
        name: 'documentView',
        params: { procedureId: data.procedureId, documentId: data.documentId }
      });
    }
  },

  beforeRouteUpdate(to, from, next) {
    const { paymentDate } = this.$schema.submit.$getData();
    getPaymentList(to, paymentDate).then(() => next());
  }
};
</script>

<style>
.payment-list-toolbar {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.payment-list-toolbar div {
  vertical-align: top;
}

.payment-list .payment-pack-picker {
  min-width: 300px;
}

.payment-list .month-select {
  width: 120px;
}
</style>
